import { useCallback, useEffect, useState } from 'react';

import { useGenerateSetOfImages } from 'src/modules/homeownerApp/hooks';
import { CreatedFrom } from 'src/modules/homeownerApp/static_values';
import {
  type AiOrSampleOriginalImage,
  isSample,
} from 'src/modules/homeownerApp/types';
import type { AiOriginalImageFragment } from 'src/shared/graphql/generated';

import type { AiDisplayImageFragment } from '../graphql/generated';

type AiOriginalImageWithDisplayImages = AiOriginalImageFragment & {
  aiDisplayImages?: AiDisplayImageFragment[];
};

const hasStyledDisplayImages = (original: AiOriginalImageWithDisplayImages) =>
  !!original.aiDisplayImages?.some(
    displayImage => displayImage.createdFrom === CreatedFrom.STYLE,
  );

const hasGeneratedImages = (originalImage: AiOrSampleOriginalImage) => {
  if (isSample(originalImage)) return false;
  return hasStyledDisplayImages(originalImage);
};

/**
 * Hook for generating an initial batch of images.
 *
 * @param originalImage - The original image to generate the batch from.
 * @param style - The style to apply to the generated images.
 * @param batchSize - The number of images to generate in the batch.
 */
const useGenerateInitialBatch = (
  originalImage: AiOrSampleOriginalImage,
  style: string,
  batchSize: number,
) => {
  const isSampleImage = isSample(originalImage);

  const [generatedInitialBatch, setGeneratedInitialBatch] = useState(false);
  const [generateSetOfImages, _generateResult, isCallbackReady] =
    useGenerateSetOfImages(originalImage);

  const generateBatch = useCallback(() => {
    void generateSetOfImages(style, batchSize);
  }, [batchSize, generateSetOfImages, style]);

  useEffect(() => {
    if (
      !isSampleImage &&
      !generatedInitialBatch &&
      !hasGeneratedImages(originalImage) &&
      isCallbackReady
    ) {
      void generateBatch();
      setGeneratedInitialBatch(true);
    }
  }, [
    generateBatch,
    generatedInitialBatch,
    isSampleImage,
    isCallbackReady,
    originalImage,
  ]);
};

export { useGenerateInitialBatch };
